import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, ActivitySquare } from "lucide-react";
import { api, resolveMedia } from "../../lib/api";
import { inputCls, errMsg, CategorySelect, UploadPicker } from "./shared";

const EMPTY = { title_hi: "", title_en: "", desc_hi: "", desc_en: "", image: "", category: "", order: 0, active: true };

export default function ActivitiesManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get("/admin/activities").then((r) => setItems(r.data.items || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/admin/activities/${editing}`, form);
        toast.success("गतिविधि अपडेट हुई");
      } else {
        await api.post("/admin/activities", { ...form, order: items.length });
        toast.success("गतिविधि जोड़ी गई — वेबसाइट पर दिखने लगी है");
      }
      setForm(EMPTY);
      setEditing(null);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("क्या आप यह गतिविधि हटाना चाहते हैं?")) return;
    try {
      await api.delete(`/admin/activities/${id}`);
      setItems((p) => p.filter((i) => i.id !== id));
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const move = async (idx, dir) => {
    const swap = items[idx + dir];
    if (!swap) return;
    const a = items[idx];
    try {
      await api.put(`/admin/activities/${a.id}`, { ...a, order: swap.order });
      await api.put(`/admin/activities/${swap.id}`, { ...swap, order: a.order });
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const toggleActive = async (a) => {
    try {
      await api.put(`/admin/activities/${a.id}`, { ...a, active: !a.active });
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div>
      <button data-testid="activity-new" onClick={() => { setForm(EMPTY); setEditing(null); setShowForm(!showForm); }} className="mb-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-ngo-deep text-white font-bold hover:bg-ngo-forest transition-colors duration-300">
        <Plus size={16} /> नई मुख्य गतिविधि जोड़ें
      </button>

      {showForm && (
        <form data-testid="activity-form" onSubmit={save} className="bg-white rounded-2xl border border-ngo-deep/10 p-6 mb-8 grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">गतिविधि शीर्षक (हिंदी) *</label>
            <input data-testid="activity-title-hi" required className={inputCls} value={form.title_hi} onChange={(e) => setForm({ ...form, title_hi: e.target.value })} />
          </div>
          <div>
            <label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">शीर्षक (English)</label>
            <input data-testid="activity-title-en" className={inputCls} value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} />
          </div>
          <div>
            <label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">विवरण (हिंदी)</label>
            <textarea data-testid="activity-desc-hi" rows={2} className={inputCls} value={form.desc_hi} onChange={(e) => setForm({ ...form, desc_hi: e.target.value })} />
          </div>
          <div>
            <label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">विवरण (English)</label>
            <textarea data-testid="activity-desc-en" rows={2} className={inputCls} value={form.desc_en} onChange={(e) => setForm({ ...form, desc_en: e.target.value })} />
          </div>
          <div>
            <label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">श्रेणी</label>
            <CategorySelect testid="activity-category" value={form.category} onChange={(v) => setForm({ ...form, category: v })} allowEmpty />
          </div>
          <div>
            <label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">गतिविधि फोटो</label>
            <div className="flex gap-2 items-center">
              <input data-testid="activity-image" className={inputCls} value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="फोटो अपलोड करें या URL डालें" />
              <UploadPicker testid="activity-image-upload" context="activities" label="अपलोड" onUploaded={(url) => setForm((p) => ({ ...p, image: url }))} />
            </div>
            {form.image && <img src={resolveMedia(form.image)} alt="गतिविधि" className="mt-2 h-24 rounded-xl object-cover border border-ngo-deep/10" />}
          </div>
          <label className="flex items-center gap-2.5 text-sm font-semibold text-ngo-ink">
            <input data-testid="activity-active" type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-[18px] h-[18px] accent-ngo-deep" />
            वेबसाइट पर दिखाएं (सक्रिय)
          </label>
          <div className="sm:col-span-2 flex gap-3">
            <button data-testid="activity-save" className="px-7 py-3 rounded-full bg-ngo-deep text-white font-bold hover:bg-ngo-forest transition-colors duration-300">{editing ? "अपडेट करें" : "गतिविधि जोड़ें"}</button>
            <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="px-6 py-3 rounded-full border border-ngo-deep/20 text-ngo-deep text-sm font-semibold">रद्द करें</button>
          </div>
        </form>
      )}

      {items.length === 0 && <p data-testid="activity-empty" className="text-center text-ngo-ink/50 py-14">अभी कोई गतिविधि नहीं — वेबसाइट पर डिफ़ॉल्ट गतिविधियां दिख रही हैं। नई जोड़ते ही वही दिखेंगी।</p>}
      <div data-testid="activity-list" className="space-y-4">
        {items.map((a, idx) => (
          <div key={a.id} className="bg-white rounded-2xl border border-ngo-deep/10 p-4 flex flex-wrap items-center gap-4">
            {a.image ? (
              <img src={resolveMedia(a.image)} alt={a.title_hi} className="w-24 h-16 object-cover rounded-lg" />
            ) : (
              <div className="w-24 h-16 rounded-lg bg-ngo-mint flex items-center justify-center text-ngo-deep"><ActivitySquare size={20} /></div>
            )}
            <div className="flex-1 min-w-[200px]">
              <p className="font-semibold text-ngo-ink">{a.title_hi}</p>
              <p className="text-xs text-ngo-ink/50 mt-1">{a.active ? <span className="text-ngo-deep font-bold">● सक्रिय</span> : <span className="text-ngo-ink/40 font-bold">● निष्क्रिय</span>}{a.category && ` • ${a.category}`}</p>
              {a.desc_hi && <p className="text-xs text-ngo-ink/60 mt-1 line-clamp-1">{a.desc_hi}</p>}
            </div>
            <div className="flex gap-1.5">
              <button data-testid={`activity-up-${a.id}`} onClick={() => move(idx, -1)} disabled={idx === 0} aria-label="ऊपर" className="w-9 h-9 rounded-full bg-ngo-mint text-ngo-deep flex items-center justify-center disabled:opacity-30 hover:bg-ngo-leaf/25 transition-colors duration-200"><ArrowUp size={15} /></button>
              <button data-testid={`activity-down-${a.id}`} onClick={() => move(idx, 1)} disabled={idx === items.length - 1} aria-label="नीचे" className="w-9 h-9 rounded-full bg-ngo-mint text-ngo-deep flex items-center justify-center disabled:opacity-30 hover:bg-ngo-leaf/25 transition-colors duration-200"><ArrowDown size={15} /></button>
              <button data-testid={`activity-toggle-${a.id}`} onClick={() => toggleActive(a)} className={`px-4 h-9 rounded-full text-xs font-bold transition-colors duration-200 ${a.active ? "bg-ngo-deep text-white" : "bg-ngo-ink/10 text-ngo-ink/60"}`}>{a.active ? "सक्रिय" : "निष्क्रिय"}</button>
              <button data-testid={`activity-edit-${a.id}`} onClick={() => { setEditing(a.id); setForm({ title_hi: a.title_hi, title_en: a.title_en || "", desc_hi: a.desc_hi || "", desc_en: a.desc_en || "", image: a.image || "", category: a.category || "", order: a.order, active: a.active }); setShowForm(true); window.scrollTo({ top: 0, behavior: "smooth" }); }} aria-label="संपादित करें" className="w-9 h-9 rounded-full bg-ngo-mint text-ngo-deep flex items-center justify-center hover:bg-ngo-leaf/25 transition-colors duration-200"><Pencil size={15} /></button>
              <button data-testid={`activity-delete-${a.id}`} onClick={() => remove(a.id)} aria-label="हटाएं" className="w-9 h-9 rounded-full bg-red-50 text-red-600 flex items-center justify-center hover:bg-red-100 transition-colors duration-200"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
